import { useState } from 'react';
import { receiptsService } from '../../services/api';
import { fmtDate } from '../../utils/businessCosts';
import './ReceiptThumbs.css';

/** "Bunnings · 13 Sep 2026 · 5 lines" — how a receipt is named wherever it shows. */
export const receiptLabel = (r) => `${r.vendor || 'Receipt'} · ${fmtDate(r.receipt_date)} · ${r.line_count} line${r.line_count === 1 ? '' : 's'}`;

/**
 * The receipt on a row (backlog #687, GID-Session-018): the docket's page
 * thumbnails — the same photo every other line on that docket shows — a
 * camera to add pages (or make a receipt for a row that has none), a
 * paperclip to put a receipt already uploaded on this row, and × to take it
 * off again. The photo stays with the other lines; it goes with the last.
 *
 * `kind` is 'expense' | 'job_cost' | 'equipment'; `onChange` is called after
 * anything changes so the page reloads its rows; `onLightbox(images, index)`
 * opens the page's lightbox.
 */
export default function ReceiptThumbs({ kind, rowId, receipt, onChange, onLightbox }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [options, setOptions] = useState(null); // null = picker closed; [] = loading / nothing to pick

  const fail = (r, fallback) => setError(r?.error || fallback);

  async function upload(files) {
    if (!files?.length) return;
    setBusy(true); setError('');
    const r = await receiptsService.uploadFor(kind, rowId, [...files]);
    setBusy(false);
    if (r.success) onChange?.(); else fail(r, 'Receipt upload failed');
  }
  async function openPicker() {
    setError('');
    setOptions([]);
    const r = await receiptsService.list({ limit: 40 });
    if (r.success) setOptions(r.receipts || []); else { setOptions(null); fail(r, 'Could not load the receipts'); }
  }
  async function attach(receiptId) {
    if (!receiptId) { setOptions(null); return; }
    setBusy(true); setError('');
    const r = await receiptsService.attach(Number(receiptId), kind, rowId);
    setBusy(false);
    setOptions(null);
    if (r.success) onChange?.(); else fail(r, 'Could not put the receipt on this line');
  }
  async function detach() {
    const others = (receipt?.line_count || 1) - 1;
    const q = others > 0
      ? `Take the receipt off this line? It stays with the other ${others} line${others === 1 ? '' : 's'} on it.`
      : 'Take the receipt off this line? It is the only line on it, so the photo will be deleted.';
    if (!window.confirm(q)) return;
    setBusy(true); setError('');
    const r = await receiptsService.detach(kind, rowId);
    setBusy(false);
    if (r.success) onChange?.(); else fail(r, 'Could not take the receipt off');
  }
  function open(index) {
    if (!receipt?.pages?.length) return;
    const label = receiptLabel(receipt);
    onLightbox?.(receipt.pages.map((p) => ({ src: receiptsService.pageUrl(p.id), label: receipt.pages.length > 1 ? `${label} — page ${p.page_no}` : label })), index);
  }

  const pages = receipt?.pages || [];
  const shared = receipt && receipt.line_count > 1;
  return (
    <span className={`rt ${busy ? 'rt-busy' : ''}`}>
      {receipt && (
        <span className="rt-receipt" title={receiptLabel(receipt)}>
          {pages.map((p, i) => (
            <button key={p.id} type="button" className="rt-open" aria-label={`Open receipt page ${p.page_no}`} onClick={() => open(i)}>
              <img src={receiptsService.pageUrl(p.id, 'thumb')} alt={p.original_name || `Receipt page ${p.page_no}`} />
            </button>
          ))}
          {pages.length === 0 && <span className="rt-nophoto text-muted">no photo yet</span>}
          {shared && <span className="rt-shared text-muted">{receipt.line_count} lines</span>}
          <button type="button" className="rt-remove" aria-label="Take the receipt off this line" title="Take the receipt off this line" onClick={detach} disabled={busy}>×</button>
        </span>
      )}
      <label className={`rt-btn ${busy ? 'disabled' : ''}`} title={receipt ? 'Add another page of this receipt' : 'Add a receipt photo'}>
        📎
        <input type="file" accept="image/*" multiple hidden disabled={busy} aria-label={receipt ? 'Add a receipt page' : 'Add a receipt photo'} onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
      </label>
      {!receipt && options === null && (
        <button type="button" className="rt-btn" title="Use a receipt already uploaded — the same docket as other lines" aria-label="Use a receipt already uploaded" onClick={openPicker} disabled={busy}>🔗</button>
      )}
      {options !== null && (
        <select className="rt-picker" aria-label="Receipt already uploaded" defaultValue="" onChange={(e) => attach(e.target.value)} onBlur={() => setOptions(null)} autoFocus disabled={busy}>
          <option value="">{options.length ? 'Which receipt?' : 'No receipts uploaded yet'}</option>
          {options.map((r) => <option key={r.id} value={r.id}>{receiptLabel(r)}</option>)}
        </select>
      )}
      {busy && <span className="rt-note text-muted">Working…</span>}
      {error && <span className="rt-error">{error}</span>}
    </span>
  );
}
